import React from "react";
import { useSelector } from "react-redux";
import {
	selectPipelines,
	selectSolverStatuses,
	selectTaskTypes,
} from "./slice";

import TextFilter from "../filters/TextFilter";
import DateTimeFilter from "../filters/DateTimeFilter";
import { selectRSOTypes } from "../rsos/slice";
import Table, { Row, Cell, NoDataCell } from "../../ui/Table";
import TableHeaderRow from "../../ui/TableHeaderRow";
import TableHeader from "../../ui/TableHeader";
import DropDownFilter from "../filters/DropDownFilter";
import RSOType from "../rsos/RSOType";
import RSOFilter from "../rsos/filters/RSOFilter";
import SortHeader from "../sorting/SortHeader";
import ID from "../../ui/ID";
import Timestamp from "../../ui/Timestamp";
import PropTypes from "prop-types";
import { Link, useNavigate } from "react-router-dom";
import IDCopy from "../../ui/IDCopy";
import { selectAstrositesById } from "../astrosites/slice";
import TagName from "../tags/TagName";
import { selectAdvancedMode } from "../settings/slice";
import AstrositeName from "../astrosites/AstrositeName";
import RSOInfo from "../rsos/RSOInfo";
import { getObjectCountTag, getSolverStatusTag } from "./tagUtils";
import Accepted from "./Accepted";
import ObjectCount from "./ObjectCount";
import { updateSearchParamsWithFilters } from "../filters/filters";
import SolveStatus from "./SolveStatus";
import styled from "styled-components";

// When displaying observation tags, hide these from view as their
// presence does not convey any meaning - they are instead usually
// present on all observations because of the data they contain.
const HIDDEN_TAGS = new Set([
	"solve-satellites-count",
	"field_of_view_satellites",
	"solve",
]);

/**
 * ObservationsTable renders a given list of observations.
 * @param observations
 * @param filter If provided will be used to power the filters if not filters will not be shown
 * @param setFilter
 * @param collectionId  If provided indicates that we are showing observations in the context of a particular collection, as such the link to each individual collection will also be passed the id
 * @returns {JSX.Element}
 * @constructor
 */
const ObservationsTable = ({
	observations = [],
	filter = {},
	setFilter = () => {},
	collectionId,
	hideFilters = false,
	hideObject = false,
	selected = null,
	setSelected = null,
}) => {
	//const tagsLoadStatus = useSelector(selectTagsLoadStatus)
	const rosTypes = useSelector(selectRSOTypes); // The various types of RSO
	const astrositesByIds = useSelector(selectAstrositesById);
	// The various known pipelines
	const pipelines = useSelector(selectPipelines);
	// The various known solve status
	const solveStatuses = useSelector(selectSolverStatuses);
	// the different task types
	const taskTypes = useSelector(selectTaskTypes);

	const advancedModeState = useSelector(selectAdvancedMode);
	// if filters apply to advancedMode columns we have to show them
	const advancedModeOverride = filter.pipeline_id || filter.id_cnts;
	const advancedMode = advancedModeOverride || advancedModeState;

	// If we have any filters, converts them to a query string. Used to pass through
	// to observationLink to provide in-context navigation.
	const obsLinkQueryString = (filter = {}) => {
		const params = new URLSearchParams();
		updateSearchParamsWithFilters(params, filter);
		const ps = params.toString();
		// If we have filters, construct a link to browse in the context thereof
		return ps ? `?nav_in_list=true&${ps}` : "";
		// To not show 'navigate within list' top bar: return ps ? `?${ps}` : ""
	};

	// Determines how and individual observation is navigated to.
	// Supports additional params for contextual browsing, either in the
	// context of the current filtered set, or in the context of a collection
	const observationLink = (observationID) =>
		collectionId
			? `/observations/${observationID}?collection_id=${collectionId}`
			: `/observations/${observationID}${obsLinkQueryString(filter)}`;

	const navigate = useNavigate();
	const navigateTo = (observation_id) => () =>
		navigate(observationLink(observation_id));

	// Selection behaviour
	const toggleSelected = (id) => () => {
		if (selected == null) {
			return;
		}
		if (selected.has(id)) {
			selected.delete(id);
		} else {
			selected.add(id);
		}
		setSelected(new Set(selected));
	};
	const isAllSelected =
		observations?.length > 0 && selected?.size === observations?.length;
	const toggleSelectAll = () => {
		if (selected == null) {
			return;
		}
		if (selected.size < observations?.length) {
			setSelected(new Set(observations.map((o) => o.id)));
		} else {
			setSelected(new Set());
		}
	};

	return (
		<Table>
			<TableHeaderRow sticky>
				{selected != null && (
					<TableHeader>
						<input
							type="checkbox"
							checked={isAllSelected}
							onChange={toggleSelectAll}
						/>
					</TableHeader>
				)}
				{!hideFilters ? (
					<TableHeader
						filterControl={
							<DateTimeFilter
								filter={filter}
								setFilter={setFilter}
								active={filter.from || filter.to ? true : false}
							/>
						}
						sortControl={
							<SortHeader
								setFilter={setFilter}
								filter={filter}
								sortBy={"timestamp"}
							/>
						}
					>
						Date & Time
					</TableHeader>
				) : (
					<TableHeader>Date & Time</TableHeader>
				)}

				{advancedMode && (
					<TableHeader
						filterControl={
							(!hideFilters && (
								<TextFilter
									filterByName="id_cnts"
									filter={filter}
									setFilter={setFilter}
									active={filter.id}
								/>
							)) ||
							undefined
						}
						sortControl={
							(!hideFilters && (
								<SortHeader
									setFilter={setFilter}
									filter={filter}
									sortBy={"observation_id"}
								/>
							)) ||
							undefined
						}
					>
						ID
					</TableHeader>
				)}
				<TableHeader
					filterControl={
						(!hideFilters && (
							<DropDownFilter
								values={Object.keys(astrositesByIds).map((a) => ({
									name: a,
									value: a,
								}))}
								filterByName="astrosite_id"
								filter={filter}
								setFilter={setFilter}
								active={filter.astrosite_id ? true : false}
							/>
						)) ||
						undefined
					}
					sortControl={
						(!hideFilters && (
							<SortHeader
								setFilter={setFilter}
								filter={filter}
								sortBy={"astrosite_id"}
							/>
						)) ||
						undefined
					}
				>
					Astrosite
				</TableHeader>
				{!hideObject && (
					<TableHeader
						filterControl={
							(!hideFilters && (
								<RSOFilter
									filter={filter}
									setFilter={setFilter}
									textEntryDebounceMs={100}
									active={filter.object_name ? true : false}
								/>
							)) ||
							undefined
						}
						sortControl={
							(!hideFilters && (
								<SortHeader
									setFilter={setFilter}
									filter={filter}
									sortBy={"object_name"}
								/>
							)) ||
							undefined
						}
					>
						Object
					</TableHeader>
				)}
				<TableHeader
					filterControl={
						(!hideFilters && (
							<DropDownFilter
								values={rosTypes.map((rso) => {
									return { name: rso, value: rso };
								})}
								filterByName="object_type"
								filter={filter}
								setFilter={setFilter}
								active={filter.object_type ? true : false}
							/>
						)) ||
						undefined
					}
					sortControl={
						(!hideFilters && (
							<SortHeader
								setFilter={setFilter}
								filter={filter}
								sortBy={"object_type"}
							/>
						)) ||
						undefined
					}
				>
					Object type
				</TableHeader>
				<TableHeader
					filterControl={
						(!hideFilters && (
							<DropDownFilter
								values={taskTypes.map((taskType) => {
									return { name: taskType, value: taskType };
								})}
								filterByName="task_type"
								filter={filter}
								setFilter={setFilter}
								active={filter.task_type ? true : false}
							/>
						)) ||
						undefined
					}
				>
					Task type
				</TableHeader>
				{advancedMode && (
					<TableHeader
						filterControl={
							(!hideFilters && (
								<DropDownFilter
									values={
										pipelines && pipelines.map((p) => ({ name: p, value: p }))
									}
									filterByName="pipeline_id"
									filter={filter}
									setFilter={setFilter}
									active={filter.pipeline_id ? true : false}
								/>
							)) ||
							undefined
						}
						sortControl={
							(!hideFilters && (
								<SortHeader
									setFilter={setFilter}
									filter={filter}
									sortBy={"pipeline_id"}
								/>
							)) ||
							undefined
						}
					>
						Pipeline
					</TableHeader>
				)}
				<TableHeader
					filterControl={
						(!hideFilters && (
							<DropDownFilter
								values={solveStatuses?.map((p) => ({ name: p, value: p }))}
								filterByName="solve_status"
								filter={filter}
								setFilter={setFilter}
								active={filter.solve_status ? true : false}
							/>
						)) ||
						undefined
					}
				>
					Solve Status
				</TableHeader>
				<TableHeader
					filterControl={
						(!hideFilters && (
							<DropDownFilter
								values={[
									{
										name: "accepted",
										value: true,
									},
									{
										name: "declined",
										value: false,
									},
								]}
								filterByName="accepted"
								filter={filter}
								setFilter={setFilter}
								active={filter.accepted ? true : false}
							/>
						)) ||
						undefined
					}
				>
					Accepted
				</TableHeader>
				<TableHeader
					filterControl={
						<TextFilter
							filterByName="object_count"
							filter={filter}
							setFilter={setFilter}
							placeholder="2, <3, >=4, 6-7"
							toolTip="Number of detected objects, specified as number, range or inequality."
							active={filter.object_count ? true : false}
							showClear={false}
						/>
					}
				>
					Object count
				</TableHeader>
				<TableHeader>Tags</TableHeader>
				<TableHeader>UDL status</TableHeader>
				<TableHeader />
			</TableHeaderRow>
			{observations && (
				<tbody>
					{observations.map((observation, i) => (
						<Row
							key={i}
							zebra
							drawAttention={false /* True if just got loaded */}
						>
							{selected != null && (
								<Cell>
									<input
										type="checkbox"
										checked={selected.has(observation.id)}
										onChange={toggleSelected(observation.id)}
									/>
								</Cell>
							)}
							<Cell onClick={navigateTo(observation.id)}>
								<Timestamp utc={advancedMode} columnMode={true}>
									{observation.timestamp}
								</Timestamp>
							</Cell>
							{advancedMode && (
								<Cell onClick={navigateTo(observation.id)}>
									<IDCopy truncateTo={20} id={observation.id} showCopy={true} />
								</Cell>
							)}
							<Cell>
								<AstrositeName astrositeId={observation.astrosite} />
							</Cell>
							{!hideObject && (
								<Cell>
									{observation.calculated_properties &&
									observation.calculated_properties.objects ? (
										<span>
											{observation.calculated_properties.objects.map((obj) => (
												<RSOInfo
													key={obj.id}
													id={obj.id}
													object_name={
														obj.group === "exo" ? obj.iau_name : obj.object_name
													}
													object_id={obj.object_id}
													source={obj.group}
												/>
											))}
										</span>
									) : (
										<span>-</span>
									)}
								</Cell>
							)}
							<Cell onClick={navigateTo(observation.id)}>
								{observation?.calculated_properties?.objects ? (
									<div>
										{observation.calculated_properties.objects.map((obj) => (
											<span key={observation.id}>
												<RSOType type={obj.object_type} printName />
											</span>
										))}
									</div>
								) : (
									<span>-</span>
								)}
							</Cell>
							<Cell onClick={navigateTo(observation.id)}>
								{observation?.calculated_properties?.tasks ? (
									<div>
										{observation.calculated_properties.tasks.map((obj) => (
											<span key={observation.id}>{obj?.payload?.type}</span>
										))}
									</div>
								) : (
									<span>-</span>
								)}
							</Cell>
							{advancedMode && (
								<Cell onClick={navigateTo(observation.id)}>
									<ID>{observation.pipeline}</ID>
								</Cell>
							)}
							<Cell onClick={navigateTo(observation.id)}>
								<SolveStatus
									status={getSolverStatusTag(observation?.tags)?.value}
								/>
							</Cell>
							<Cell onClick={navigateTo(observation.id)}>
								<Accepted {...observation} />
							</Cell>
							<Cell onClick={navigateTo(observation.id)}>
								<ObjectCount {...getObjectCountTag(observation?.tags)} />
							</Cell>
							<Cell onClick={navigateTo(observation.id)}>
								{observation?.tags
									?.filter((t) => !HIDDEN_TAGS.has(t?.name))
									.map((tag) => (
										<TagName key={tag.name} tag={tag} />
									))}{" "}
							</Cell>

							<Cell onClick={navigateTo(observation.id)}>
								<span>{observation.udl_status}</span>
							</Cell>
							<Cell>
								<Link to={observationLink(observation.id)}>Detail</Link>
							</Cell>
						</Row>
					))}
				</tbody>
			)}
			{observations != null && observations.length === 0 && (
				<Row>
					<NoDataCell colSpan="100%">No matching data</NoDataCell>
				</Row>
			)}
		</Table>
	);
};

ObservationsTable.propTypes = {
	observations: PropTypes.array, // the observations to render
	filter: PropTypes.object, // If provided will be used to power the filters if not filters will not be shown
	setFilter: PropTypes.func, // If provided will be used to power the filters if not filters will not be shown
	collectionId: PropTypes.string, // If provided indicates that we are showing observations in the context of a particular collection, as such the link to each individual collection will also be passed the id
	hideFilters: PropTypes.bool, // If provided hides the filters for the table
	hideObject: PropTypes.bool, // If provided hides the object column
	selected: PropTypes.object, // Optional<Set<ObservationID>> -  If null, no selection supported
	setSelected: PropTypes.func, // Set<ObservationID> -> ()
};

export default ObservationsTable;
